<script setup lang="ts">
import {
  DeleteGamePrize,
  GAMES_USE_MOCKS,
  GamesApiError,
  GetGamePrizes,
  GetKioskGames,
} from "@/api/gamesApi";
import ConfirmDeleteDialog from "@/components/dialogs/ConfirmDeleteDialog.vue";
import GamesDemoAlert from "@/components/games/GamesDemoAlert.vue";
import PrizeFormDialog from "@/components/games/PrizeFormDialog.vue";
import EntityListTable from "@/components/shared/EntityListTable.vue";
import EntityListToolbar from "@/components/shared/EntityListToolbar.vue";
import { useGamesFormat } from "@/composables/useGamesFormat";
import { GAMES_ADMIN_ROLES } from "@/config/moduleRegistry/keys";
import { canAnyRole, canDoctype } from "@/helper/permissionHelper";
import useToastify from "@/stores/toastify.store";
import {
  PRIZE_TYPES,
  emptyGamePrize,
  prizeTypeIcon,
  type AddEditGamePrize,
  type GamePrize,
  type KioskGame,
  type PrizeType,
} from "@/type/games";

/**
 * The prize catalogue: discounts, free items and loyalty points that games award.
 * One prize can sit on many games with different odds; the "used in" column shows
 * where, because that is also why a prize cannot simply be deleted.
 */

definePage({ meta: { permissionKey: "page.games.prizes" } });

const { t } = useI18n({ useScope: "global" });
const toast = useToastify();
const { prizeTitle, prizeValue, prizeTypeLabel, formatInteger } = useGamesFormat();

const prizes = ref<GamePrize[]>([]);
const games = ref<KioskGame[]>([]);
const loading = ref(false);
const failed = ref(false);

const canManage = computed(() => GAMES_USE_MOCKS || canDoctype("Kiosk Game Prize", "write") || canAnyRole(GAMES_ADMIN_ROLES));
const canDelete = computed(() => GAMES_USE_MOCKS || canDoctype("Kiosk Game Prize", "delete") || canAnyRole(GAMES_ADMIN_ROLES));

async function load() {
  failed.value = false;
  try {
    const [p, g] = await Promise.all([GetGamePrizes(loading), GetKioskGames(ref(false))]);
    prizes.value = p;
    games.value = g;
  }
  catch {
    failed.value = true;
  }
}

/** prize name → games that award it (as a prize or the consolation prize). */
const usage = computed(() => {
  const map = new Map<string, KioskGame[]>();
  for (const game of games.value) {
    const names = new Set([...game.prizes.map(r => r.prize), game.consolation_prize].filter(Boolean) as string[]);
    for (const name of names)
      map.set(name, [...(map.get(name) ?? []), game]);
  }

  return map;
});

const search = ref("");
const typeFilter = ref<PrizeType | null>(null);
const typeItems = computed(() => PRIZE_TYPES.map(value => ({ value, title: prizeTypeLabel(value) })));

const rows = computed(() => {
  const q = search.value.trim().toLowerCase();

  return prizes.value
    .filter(p => !typeFilter.value || p.prize_type === typeFilter.value)
    .filter(p => !q || `${p.title} ${p.title_ar} ${p.item_label}`.toLowerCase().includes(q));
});

const headers = computed(() => [
  { title: t("alkokh.games.prizeColumns.prize"), key: "title" },
  { title: t("alkokh.games.prizeColumns.type"), key: "prize_type" },
  { title: t("alkokh.games.prizeColumns.value"), key: "value" },
  { title: t("alkokh.games.prizeColumns.validDays"), key: "valid_days", align: "end" as const },
  { title: t("alkokh.games.prizeColumns.usedIn"), key: "usage", sortable: false },
  { title: t("alkokh.games.prizeColumns.status"), key: "is_active" },
  { title: t("alkokh.global.actions"), key: "actions", sortable: false, align: "end" as const },
]);

// --- Add / edit -------------------------------------------------------------------

const formOpen = ref(false);
const formPrize = ref<AddEditGamePrize>(emptyGamePrize());

function openAdd() {
  formPrize.value = emptyGamePrize();
  formOpen.value = true;
}

function openEdit(prize: GamePrize) {
  formPrize.value = JSON.parse(JSON.stringify(prize));
  formOpen.value = true;
}

// --- Delete --------------------------------------------------------------------

const deleteOpen = ref(false);
const pendingDelete = ref<GamePrize | null>(null);

function requestDelete(prize: GamePrize) {
  const usedBy = usage.value.get(prize.name);
  if (usedBy?.length) {
    // Said before the confirm, not after it: there is nothing to confirm.
    toast.warning({ text: t("alkokh.games.errors.prize_in_use", { games: usedBy.map(g => g.title).join(", ") }) });

    return;
  }
  pendingDelete.value = prize;
  deleteOpen.value = true;
}

async function confirmDelete() {
  const prize = pendingDelete.value;
  deleteOpen.value = false;
  pendingDelete.value = null;
  if (!prize)
    return;
  try {
    await DeleteGamePrize(ref(false), prize.name);
    toast.success({ text: t("alkokh.games.messages.prizeDeleted") });
    await load();
  }
  catch (error: any) {
    const text = error instanceof GamesApiError
      ? t(`alkokh.games.errors.${error.code}`, { games: error.message })
      : error?.message || t("alkokh.games.messages.deleteFailed");
    toast.error({ text });
  }
}

onMounted(load);
</script>

<template>
  <div class="prizes-page">
    <div class="prizes-page__head">
      <div>
        <h1 class="text-h4">
          {{ t("alkokh.games.prizes.title") }}
        </h1>
        <p class="text-body-2 text-medium-emphasis mb-0">
          {{ t("alkokh.games.prizes.subtitle") }}
        </p>
      </div>
      <VBtn
        v-if="canManage"
        color="primary"
        prepend-icon="tabler-plus"
        @click="openAdd"
      >
        {{ t("alkokh.games.actions.newPrize") }}
      </VBtn>
    </div>

    <GamesDemoAlert @reset="load" />

    <VAlert
      v-if="failed"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      {{ t("alkokh.games.messages.loadFailed") }}
      <template #append>
        <VBtn
          size="small"
          variant="text"
          @click="load"
        >
          {{ t("alkokh.global.retry") }}
        </VBtn>
      </template>
    </VAlert>

    <VCard elevation="0">
      <EntityListToolbar
        :search="search"
        :search-placeholder="t('alkokh.global.search')"
        :show-add-button="false"
        @update:search="search = $event"
      >
        <template #filters>
          <AppSelect
            v-model="typeFilter"
            :items="typeItems"
            clearable
            density="compact"
            :placeholder="t('alkokh.games.prizes.allTypes')"
            style="min-inline-size: 200px;"
          />
        </template>
      </EntityListToolbar>

      <VDivider />

      <VCardText>
        <EntityListTable
          :headers="headers"
          :items="rows"
          :items-length="rows.length"
          :loading="loading"
          client
          :items-per-page="10"
          :items-per-page-options="[10, 25, 50]"
          item-value="name"
          :filtered="Boolean(search || typeFilter)"
          :no-data-text="t('alkokh.games.prizes.empty')"
          class="text-no-wrap"
        >
          <template #item.title="{ item }">
            <div class="prizes-page__name">
              <span
                class="prizes-page__swatch"
                :style="{ background: item.color }"
              />
              <span>{{ prizeTitle(item) }}</span>
            </div>
          </template>

          <template #item.prize_type="{ item }">
            <span class="d-inline-flex align-center gap-2">
              <VIcon
                :icon="prizeTypeIcon(item.prize_type)"
                size="16"
              />
              {{ prizeTypeLabel(item.prize_type) }}
            </span>
          </template>

          <template #item.value="{ item }">
            <span class="font-weight-medium">{{ prizeValue(item) }}</span>
          </template>

          <template #item.valid_days="{ item }">
            {{ t("alkokh.games.prizes.days", { n: formatInteger(item.valid_days) }) }}
          </template>

          <template #item.usage="{ item }">
            <template v-if="usage.get(item.name)?.length">
              <VChip
                v-for="game in usage.get(item.name)"
                :key="game.name"
                size="x-small"
                label
                class="me-1"
                :to="`/games/${encodeURIComponent(game.name)}`"
              >
                {{ game.title }}
              </VChip>
            </template>
            <span
              v-else
              class="text-medium-emphasis"
            >—</span>
          </template>

          <template #item.is_active="{ item }">
            <VChip
              :color="item.is_active ? 'success' : 'secondary'"
              size="small"
              label
            >
              {{ item.is_active ? t("alkokh.games.prizes.active") : t("alkokh.games.prizes.inactive") }}
            </VChip>
          </template>

          <template #item.actions="{ item }">
            <VBtn
              v-if="canManage"
              icon
              variant="text"
              size="small"
              :aria-label="t('alkokh.global.edit')"
              @click="openEdit(item)"
            >
              <VIcon icon="tabler-pencil" />
            </VBtn>
            <VBtn
              v-if="canDelete"
              icon
              variant="text"
              size="small"
              color="error"
              :aria-label="t('alkokh.global.delete')"
              @click="requestDelete(item)"
            >
              <VIcon icon="tabler-trash" />
            </VBtn>
          </template>
        </EntityListTable>
      </VCardText>
    </VCard>

    <PrizeFormDialog
      v-model="formOpen"
      v-model:prize="formPrize"
      @saved="load"
    />

    <ConfirmDeleteDialog
      :is-dialog-visible="deleteOpen"
      :confirm-title="t('alkokh.games.delete.prizeTitle')"
      :confirm-msg="t('alkokh.games.delete.prizeMessage', { name: pendingDelete?.title ?? '' })"
      @update:is-dialog-visible="deleteOpen = $event"
      @confirm="confirmDelete"
    />
  </div>
</template>

<style scoped>
.prizes-page__head {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
  margin-block-end: 16px;
}

.prizes-page__name {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.prizes-page__swatch {
  flex: none;
  inline-size: 12px;
  block-size: 12px;
  border-radius: 3px;
}
</style>
